import { Component, OnInit, AfterViewInit, ViewChild, Output, OnChanges, Input, EventEmitter, ChangeDetectionStrategy, inject } from '@angular/core';
import { Partner } from './models/partner';
import { PartnerContactService } from './services/partner-contact.service';
import { PartnerContact } from './models/partner-contact';
import { PartnerContactType } from './models/partner-contact-type';
import { CurrentUserService } from '../user/services/current-user.service';
import { ToastService, ToastType } from '../shared/toast/toast.service';
import { CountryService } from '../shared/iso/services';
import { Country } from '../shared/iso/models';
import { DxDataGridModule } from 'devextreme-angular';
import { BizDataGridDirective } from '../shared/grid/biz-grid.directive';
import { DxoSelectionModule, DxoEditingModule, DxiColumnModule, DxoHeaderFilterModule } from 'devextreme-angular/ui/nested';
import TagBox from 'devextreme/ui/tag_box';
import Lookup from 'devextreme/ui/lookup';

import { DxTemplateModule } from 'devextreme-angular/core';

@Component({
    templateUrl: './partner-contact.component.html',
    selector: 'ep-partner-contacts',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [DxDataGridModule, BizDataGridDirective, DxoSelectionModule, DxoEditingModule, DxiColumnModule, DxoHeaderFilterModule, DxTemplateModule]
})

export class PartnerContactComponent implements OnInit, OnChanges, AfterViewInit {

    private readonly partnerContactService = inject(PartnerContactService);
    private readonly currentUserService = inject(CurrentUserService);
    private readonly toastService = inject(ToastService);   
    private readonly countryService = inject(CountryService);

    public contactTypes: PartnerContactType[] = [];
    public countries: Country[] = [];
    public isLoading: boolean;

    public allowUpdating = false;
    public allowAdding = false;
    public allowDeleting = false;

    @Input() selectedPartner: Partner;  // For permission checking only - special handling of internal partners
    @Input() selectedPartnerId: number;
    @Input() stateStorageKey: string;
    @Input() selectionMode = 'single';
    @Input() editMode = 'form';
    @Input() selectedContactIds: number[] = [];
    @Input() readonly = false;

    @Output() selectedContactsChange = new EventEmitter<PartnerContact[]>();
    @Input() selectedContacts: PartnerContact[];

    // These properties can be used if contacts should be handled different from the build-in contact handling...
    @Input() allowEditDefaultColumns = true;
    @Input() contacts: PartnerContact[];
    @Input() additionalColumns: any[] = [];
    /** If this value is true, no internal save will be performed. */
    @Input() handleSaveExternal = false;
    /** Called when handleSaveExternal is set to true and the save-button has been pressed. */
    @Output() contactSaved = new EventEmitter<PartnerContact>();

    @ViewChild('columnContactTypes', { static: true }) columnContactTypes: any;
    @ViewChild('columnCountries', { static: true }) columnCountries: any;

    ngOnInit(): void {
        this.checkPermissions();
    }

    checkPermissions() {
        let hasInternalPartnerPermission = this.currentUserService.hasPermission('Partner_Action_InternalPartner');
        if (this.selectedPartner && this.selectedPartner.PartnerType && this.selectedPartner.PartnerType.Name != 'Internal') {
            // For non-internal partners, ignore the 'internal' permission by always granting it.
            hasInternalPartnerPermission = true;
        }

        this.allowUpdating = hasInternalPartnerPermission && this.currentUserService.hasPermission('Partner_Action_GeneralEdit') && !this.readonly;
        this.allowAdding = hasInternalPartnerPermission && this.currentUserService.hasPermission('Partner_Action_GeneralEdit') && this.allowEditDefaultColumns && !this.readonly;
        this.allowDeleting = hasInternalPartnerPermission && this.currentUserService.hasPermission('Partner_Action_GeneralEdit') && this.allowEditDefaultColumns && !this.readonly;
    }

    ngAfterViewInit() {
        this.columnContactTypes.editCellTemplate = (container, options) => {
            new TagBox(container, {
                placeholder: 'Select types',
                dataSource: this.contactTypes,
                value: this.contactTypes.filter(ct => options.data.ContactTypes.some(c => ct.Id == c.Id)),
                showSelectionControls: true,
                displayExpr: 'Name',
                onValueChanged: (e) => {
                    options.setValue(e.value);
                }
            });
        };

        this.columnCountries.editCellTemplate = (container, options) => {
             new Lookup(container, {
                placeholder: 'Select country',
                dataSource: this.countries,
                valueExpr: 'this',
                value: options.data.Country ? this.countries.find(c => c.Id == options.data.Country.Id) : null, // For some reason the object-reference is lost in the flow - so if we don't take the object from the array it won't be selected in the dropdown
                displayExpr: 'FriendlyName',
                showCancelButton: true,
                showClearButton: true,
                onValueChanged: (e) => {
                    options.setValue(e.value);
                }
            });
        };
    }

    ngOnChanges(changes: any) {
        if (changes.selectedPartnerId && this.selectedPartnerId) {
            void this.loadContacts();
        }
        if (changes.contacts && this.contacts) {
            void this.loadContacts(false);
        }
        if (changes.readonly || changes.selectedPartner) {
            this.checkPermissions();
        }
    }

    public async refresh(): Promise<void> {
        await this.loadContacts(true);
    }

    async loadContacts(loadContacts = true): Promise<void> {
        try {
            this.isLoading = true;

            if (!this.selectedPartnerId) {
                loadContacts = false;
            }

            const [contacts, contactTypes, countries] = await Promise.all([
                loadContacts ? this.partnerContactService.getContactsForPartner(this.selectedPartnerId) : [] as PartnerContact[],
                this.partnerContactService.getContactTypes(),
                this.countryService.getCountries()
            ]);
            this.contacts = contacts;
            this.contactTypes = contactTypes.sort((ct1, ct2) => ct1.Name.localeCompare(ct2.Name));
            this.countries = countries.sort((c1, c2) => c1.FriendlyName.localeCompare(c2.FriendlyName));
            this.selectedContacts = this.contacts.filter(contact => this.selectedContactIds.includes(contact.ContactsId));

            this.contacts = this.contacts.map(contact => ({
                ...contact,
                ContactTypes: contact.ContactTypes.map(ct => this.contactTypes.find(act => act.Id === ct.Id) || ct),
                Country: contact.Country ? this.countries.find(c => c.Id === contact.Country.Id) || contact.Country : contact.Country
            }));
        }
        catch {
            // Ignore
        }
        finally {
            this.isLoading = false;
        }
    }

    removeContactRow(e: any) {
        const deferred = $.Deferred(); // REVIEW: Fix this when Promis/Observalbe is fully supported by DevExpress

        this.partnerContactService.deleteContact(e.data.ContactsId).then(() => {
            deferred.resolve();
            this.toastService.showToast('Contact deleted...', ToastType.success);
        }).catch(() => deferred.reject());

        e.cancel = deferred.promise();
    }

    initNewContactRow(e: any) {
        let currentMaxPriority = (this.contacts && this.contacts.length > 0) ? Math.max.apply(null, this.contacts.map((c) => c.Priority)) : 0;
        if (Number.isFinite(currentMaxPriority) || Number.isNaN(currentMaxPriority)) {
            currentMaxPriority = 0;
        }

        e.data = { PartnerId: this.selectedPartnerId, ContactTypes: [], Priority: currentMaxPriority + 10 };
    }

    handleContactSave(e: any, isInsert: boolean) {
        let partnerContactToSave: PartnerContact;

        // Find the identification-object.
        if (isInsert) {
            partnerContactToSave = e.data;
        }
        else {
            partnerContactToSave = Object.assign(e.key, e.newData);
        }

        if (this.handleSaveExternal) { // If this value is true, no internal save will be performed.
            this.contactSaved.emit(partnerContactToSave);
        }
        else {
            // REVIEW: Fix this jQuery reference when Promise/Observable is fully supported by DevExpress
            const deferred = $.Deferred();
            // This is strange - but the cancel event can be a promise, if any async events are required before saving the changes to the
            //datasource: https://js.devexpress.com/Documentation/16_2/ApiReference/UI_Widgets/dxDataGrid/Configuration/#onRowUpdating
            e.cancel = deferred.promise();

            this.partnerContactService.saveContact(partnerContactToSave).then((savedContact) => {
                Object.assign(partnerContactToSave, savedContact); // Make sure DB id's are set correctly on the object-reference
                deferred.resolve();
                this.toastService.showToast('Contact saved...', ToastType.success);
            }).catch(() => deferred.reject());
        }
    }

    // Sorting and filtering contact-types
    calculateSortValue(rowData) {
        // Sort by name - concatenate to a string
        const contactTypes: PartnerContactType[] = rowData.ContactTypes;
        return contactTypes.sort((ct1, ct2) => ct1.Name.localeCompare(ct2.Name)).map((ct) => ct.Name).join('');
    }

    getContactTypesHeaderFilterData = () =>
        (this.contacts ?? []) // Ensure contacts is defined; default to an empty array if undefined
            .flatMap(e => {
                const contacts = e.ContactTypes?.map(contactType => contactType.Name) || [];
                return contacts.length > 0 ? contacts : ['(Blanks)'];
            }) // Flatten the RoleName values from all Roles, or add '(blanks)' if no permissions
            .filter((name, idx, self) => self.indexOf(name) === idx) // Remove duplicates
            .sort((a, b) => a.localeCompare(b)) // Sort the names alphabetically
            .map(contactTypeName => ({
                text: contactTypeName,
                value: contactTypeName
            })
            )
        ;

    calculateArrayFilterExpression(filterValue: any) {
        // Custom filter for array fields
        return (rowData: any) => {
            if (filterValue === '(Blanks)') {
                // Check if there are no permissions
                return !rowData.ContactTypes || rowData.ContactTypes.length === 0;
            }
            return rowData.ContactTypes?.some((contactType: any) =>
                contactType.Name?.toLowerCase().includes(filterValue.toLowerCase())
            );
        };
    }

    calculateCountryGroupValue(rowData: any) {
        return rowData.FriendlyName;
    }

    selectionChanged(e: any) {
        this.selectedContactsChange.emit(e.selectedRowsData);
    }
}
